import { bookSlot, fetchSlots } from "./api.js";
import { cleanupCalendarPicker, syncCalendarPicker } from "./calendarPicker.js";
import { calendarStyles } from "./calendarStyles.js";
import { readConfig } from "./config.js";
import { getMessages } from "./i18n.js";
import { render } from "./render.js";
import { adaptiveStyles } from "./adaptiveStyles.js";
import { createInitialState, firstAvailableDate, updateForm } from "./state.js";
import { displayTimezone } from "./timezone.js";
import { styles } from "./styles.js";
import { validateForm } from "./validation.js";
import { feedbackStyles, revealFeedback } from "./feedback.js";
import { buildBookingPayload } from "./payload.js";

// Largeur de conteneur à partir de laquelle calendrier et créneaux sont côte à
// côte (phase 2026-10-widget-adaptatif, décision Q2).
export const WIDE_MIN_WIDTH = 600;

export function mount(target, options = {}) {
  const config = readConfig(target, options);
  const messages = getMessages(config.lang);
  const root = createRoot(config.element);
  const state = createInitialState();
  // Début de message pré-rempli (lead-gen) — texte porté par l'embed.
  if (config.messagePrefill) {
    state.form.message = config.messagePrefill;
  }
  // Fuseau d'affichage par défaut = celui du visiteur détecté (modèle Calendly).
  state.selectedTimezone = config.viewerTimezone || null;
  state.mode = measureMode(config.element);
  applyTheme(root, config);

  // En mode large, un jour est toujours sélectionné pour que les horaires
  // soient visibles sans clic (décision Q4).
  const preselectFirstDay = () => {
    if (state.mode === "wide" && !state.selectedDate && state.slots.length > 0) {
      state.selectedDate = firstAvailableDate(state.slots, displayTimezone(state, config));
      if (state.selectedDate && state.status === "idle") {
        state.status = "dateSelected";
      }
    }
  };

  const handlers = {
    onSelectTimezone(tz) {
      if (!tz || tz === state.selectedTimezone) {
        return;
      }
      // Changer de fuseau redécoupe les jours : on repart de la sélection de date.
      state.selectedTimezone = tz;
      state.selectedDate = null;
      state.selectedSlot = null;
      state.screen = "date";
      state.error = null;
      preselectFirstDay();
      rerender();
    },
    onSelectDate(date) {
      state.selectedDate = date;
      state.selectedSlot = null;
      state.screen = "slot";
      state.status = "dateSelected";
      state.error = null;
      rerender();
    },
    onSelectSlot(slot) {
      state.selectedSlot = slot;
      state.screen = "form";
      state.status = "slotSelected";
      state.error = null;
      state.fields = {};
      rerender();
    },
    onBackToSlots(form) {
      updateForm(state, form);
      state.selectedSlot = null;
      state.screen = "slot";
      state.status = "dateSelected";
      state.fields = {};
      rerender();
    },
    onBackToDate(form) {
      updateForm(state, form);
      state.selectedSlot = null;
      // Mode étroit : retour au calendrier seul. Le jour reste sélectionné.
      state.screen = "date";
      state.error = null;
      state.fields = {};
      rerender();
    },
    async onSubmit(form) {
      updateForm(state, form);
      const validation = validateForm(form, {
        collectPhone: config.collectPhone,
        phoneRequired: config.phoneRequired,
        collectMessage: config.collectMessage,
        messageRequired: config.messageRequired,
        messages
      });
      if (!validation.valid) {
        state.status = "slotSelected";
        state.error = { message: messages.validation };
        state.fields = validation.fields;
        rerender();
        revealFeedback(root, state);
        return;
      }

      state.status = "submitting";
      state.screen = "form";
      state.fields = {};
      rerender();

      try {
        const confirmation = await bookSlot(config.apiBaseUrl, config.tenantSlug, buildBookingPayload(form, state.selectedSlot, config), config.publishableKey);
        state.status = "success";
        state.screen = "success";
        state.error = null;
        state.confirmation = {
          ...confirmation,
          start: state.selectedSlot.start,
          end: state.selectedSlot.end,
          date: state.selectedSlot.date,
          label: state.selectedSlot.label,
          name: form.name,
          email: form.email
        };
      } catch (error) {
        state.error = error;
        if (error?.code === "SLOT_UNAVAILABLE") {
          state.status = "dateSelected";
          state.selectedSlot = null;
          state.screen = "slot";
        } else {
          state.status = "error";
        }
      }
      rerender();
      revealFeedback(root, state);
    }
  };

  const rerender = () => {
    render(root, state, messages, config, handlers);
    syncCalendarPicker(root, state, config, handlers, messages);
  };

  async function load() {
    state.screen = "date";
    state.status = "loading";
    rerender();

    try {
      const result = await fetchSlots(config.apiBaseUrl, config.tenantSlug, {}, config.publishableKey);
      state.slots = result.slots;
      // La timezone du tenant (renvoyée par l'API) prime sur le défaut client :
      // les heures des créneaux sont exprimées dans CE fuseau, on l'affiche tel quel.
      if (result.timezone) {
        config.timezone = result.timezone;
      }
      // Si le fuseau du visiteur n'a pas pu être détecté, on retombe sur
      // celui du commerçant comme fuseau d'affichage par défaut.
      if (!state.selectedTimezone) {
        state.selectedTimezone = config.timezone;
      }
      // Le comportement du formulaire (afficher/obligatoire) est piloté par
      // l'API (source de vérité) ; les libellés/aides restent côté embed.
      if (result.form) {
        applyFormConfig(config, result.form);
      }
      state.status = state.slots.length > 0 ? "idle" : "empty";
      state.screen = "date";
      preselectFirstDay();
    } catch (error) {
      state.screen = "date";
      state.status = "error";
      state.error = error;
    }

    rerender();
  }

  // Bascule large/étroit en direct selon la largeur réellement disponible.
  const stopObserving = observeWidth(config.element, () => {
    const mode = measureMode(config.element);
    if (mode !== state.mode) {
      state.mode = mode;
      preselectFirstDay();
      rerender();
    }
  });

  void load();

  return {
    reload: load,
    destroy() {
      stopObserving();
      cleanupCalendarPicker(root);
      const rootNode = root.getRootNode?.();
      if (rootNode && rootNode !== root && rootNode.host) {
        rootNode.innerHTML = "";
      } else {
        root.innerHTML = "";
      }
      delete config.element.dataset.randevouMounted;
    }
  };
}

// Applique le comportement de formulaire renvoyé par l'API (source de vérité).
// Les booléens `show`/`required` priment sur les valeurs de l'embed ; les
// textes (libellés, aides, pré-remplissage) restent ceux de l'embed.
function applyFormConfig(config, form) {
  if (form.phone) {
    config.collectPhone = form.phone.show;
    config.phoneRequired = form.phone.required;
  }
  if (form.message) {
    config.collectMessage = form.message.show;
    config.messageRequired = form.message.required;
  }
}

function measureMode(element) {
  const width = element?.getBoundingClientRect?.().width || 0;
  return width >= WIDE_MIN_WIDTH ? "wide" : "narrow";
}

function observeWidth(element, onChange) {
  if (typeof ResizeObserver === "undefined" || !element) {
    return () => {};
  }
  let frame = null;
  const observer = new ResizeObserver(() => {
    // Regroupe les notifications d'une même frame (redimensionnement continu).
    if (frame !== null) {
      return;
    }
    const schedule = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 16);
    frame = schedule(() => {
      frame = null;
      onChange();
    });
  });
  observer.observe(element);
  return () => observer.disconnect();
}

function createRoot(element) {
  if (element.shadowRoot) {
    element.shadowRoot.innerHTML = "";
    injectStyle(element.shadowRoot);
    return createMountPoint(element.shadowRoot);
  }

  if (element.attachShadow) {
    const shadow = element.attachShadow({ mode: "open" });
    injectStyle(shadow);
    return createMountPoint(shadow);
  }

  injectStyle(document.head);
  element.innerHTML = "";
  return element;
}

function createMountPoint(root) {
  const mountPoint = document.createElement("div");

  if (typeof mountPoint.querySelector !== "function" || typeof mountPoint.querySelectorAll !== "function") {
    return root;
  }

  if (typeof mountPoint.setAttribute === "function") {
    mountPoint.setAttribute("data-rdv-mount", "");
  }

  root.appendChild(mountPoint);
  return mountPoint;
}

function injectStyle(target) {
  for (const cssText of [styles, calendarStyles, adaptiveStyles, feedbackStyles]) {
    const style = document.createElement("style");
    style.textContent = cssText;
    target.appendChild(style);
  }
}

function applyTheme(root, config) {
  const host = root.getRootNode?.().host || root.host || root.querySelector?.(".rdv-root") || root;
  if (host?.style?.setProperty) {
    host.style.setProperty("--rdv-custom-accent", config.accentColor);
  }
}

function autoMount() {
  document.querySelectorAll("[data-yudavo-widget], #yudavo-booking").forEach((element) => {
    if (!element.dataset.randevouMounted && element.dataset.apiBaseUrl) {
      element.dataset.randevouMounted = "true";
      mount(element);
    }
  });
}

function init(options = {}) {
  return mount(options.target, options);
}

if (typeof window !== "undefined") {
  window.Randevou = { init, mount };
  window.RandevouWidget = { mount };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoMount);
  } else {
    autoMount();
  }
}
